/**
 * Leviathan Web - Inventory Detail Page
 * Quasar Prime LLC
 * Aggressive Tactical Industrial Design
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import InventoryForm from '@components/inventory/InventoryForm';
import { API_URL } from '@/config/api';

interface InventoryItem {
  id: string;
  name: string;
  sku: string;
  category: string;
  description: string | null;
  productType: string | null;
  grade: string | null;
  unit: string;
  costPerUnit: number | null;
  pricePerUnit: number | null;
  currentStock: number;
  reorderPoint: number | null;
  reorderQuantity: number | null;
  isActive: boolean;
  isTracked: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

const InventoryDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [item, setItem] = useState<InventoryItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showAdjustStock, setShowAdjustStock] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [adjustmentQty, setAdjustmentQty] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentError, setAdjustmentError] = useState('');

  const fetchItem = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/inventory/${id}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setItem(data.item);
      } else {
        setItem(null);
      }
    } catch (error) {
      console.error('Failed to fetch item:', error);
      setItem(null);
    } finally {
      setIsLoading(false);
    }
  }, [id, accessToken]);

  useEffect(() => {
    fetchItem();
  }, [fetchItem]);

  const handleDelete = async () => {
    setDeleteError('');
    try {
      const response = await fetch(`${API_URL}/api/inventory/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        navigate('/inventory');
      } else {
        const error = await response.json();
        setDeleteError(error.error || 'Failed to delete item');
      }
    } catch (error) {
      console.error('Failed to delete item:', error);
      setDeleteError('Failed to delete item');
    }
  };

  const handleAdjustStock = async () => {
    setAdjustmentError('');
    const qty = parseFloat(adjustmentQty);

    if (isNaN(qty) || qty === 0) {
      setAdjustmentError('Please enter a valid quantity');
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/inventory/${id}/adjust`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          quantity: qty,
          reason: adjustmentReason || null,
        }),
      });

      if (response.ok) {
        setShowAdjustStock(false);
        setAdjustmentQty('');
        setAdjustmentReason('');
        fetchItem();
      } else {
        const error = await response.json();
        setAdjustmentError(error.error || 'Failed to adjust stock');
      }
    } catch (error) {
      console.error('Failed to adjust stock:', error);
      setAdjustmentError('Failed to adjust stock');
    }
  };

  const getCategoryBadge = (category: string): { icon: string; label: string } => {
    const badges: Record<string, { icon: string; label: string }> = {
      FUEL: { icon: 'FUL', label: 'Fuel' },
      ADDITIVE: { icon: 'ADD', label: 'Additive' },
      LUBRICANT: { icon: 'LUB', label: 'Lubricant' },
      EQUIPMENT: { icon: 'EQP', label: 'Equipment' },
      SERVICE: { icon: 'SVC', label: 'Service' },
      OTHER: { icon: 'OTH', label: 'Other' },
    };
    return badges[category] ?? { icon: 'OTH', label: 'Other' };
  };

  const getStockStatus = () => {
    if (!item) return null;
    if (!item.isTracked) return { status: 'UNTRACKED', color: 'text-text-tertiary' };
    if (!item.reorderPoint) return { status: 'OK', color: 'text-success-green' };
    if (Number(item.currentStock) <= Number(item.reorderPoint)) {
      return { status: 'LOW STOCK', color: 'text-error-red' };
    }
    return { status: 'OK', color: 'text-success-green' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-text-tertiary font-mono animate-pulse">
          &gt; Loading product data...
        </div>
      </div>
    );
  }

  if (!item) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-error-red via-steel to-error-red" />
          <div className="border-l-4 border-error-red pl-6">
            <h1 className="text-4xl font-display font-bold text-error-red tracking-wide uppercase">
              Product Not Found
            </h1>
            <div className="flex items-center space-x-4 mt-2">
              <Link
                to="/inventory"
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
              >
                &larr; Back to Inventory
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Edit mode - show form
  if (isEditing) {
    return (
      <div className="space-y-6">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
          <div className="border-l-4 border-amber pl-6 relative">
            <h1
              className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
              style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
            >
              Edit Product
            </h1>
            <div className="flex items-center space-x-4 mt-2">
              <button
                onClick={() => setIsEditing(false)}
                className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
              >
                &larr; Cancel Editing
              </button>
            </div>
          </div>
        </div>

        <InventoryForm
          itemId={id}
          initialData={{
            name: item.name,
            sku: item.sku,
            category: item.category as 'FUEL' | 'ADDITIVE' | 'LUBRICANT' | 'EQUIPMENT' | 'SERVICE' | 'OTHER',
            description: item.description || '',
            productType: item.productType || '',
            grade: item.grade || '',
            unit: item.unit,
            costPerUnit: item.costPerUnit?.toString() || '',
            pricePerUnit: item.pricePerUnit?.toString() || '',
            currentStock: item.currentStock.toString(),
            reorderPoint: item.reorderPoint?.toString() || '',
            reorderQuantity: item.reorderQuantity?.toString() || '',
            isActive: item.isActive,
            isTracked: item.isTracked,
            notes: item.notes || '',
          }}
          onSuccess={() => {
            setIsEditing(false);
            fetchItem();
          }}
        />
      </div>
    );
  }

  const category = getCategoryBadge(item.category);
  const stockStatus = getStockStatus();

  // View mode - show details
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="relative">
        <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber shadow-[0_0_8px_rgba(255,149,0,0.6)]" />
        <div className="border-l-4 border-amber pl-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <h1
                className="text-4xl font-display font-bold text-steel tracking-wide uppercase"
                style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
              >
                {item.name}
              </h1>
              <div className="flex items-center space-x-4 mt-2">
                <Link
                  to="/inventory"
                  className="text-text-secondary text-sm font-mono hover:text-amber transition-colors"
                >
                  &larr; Back to Inventory
                </Link>
                <span className="text-text-tertiary text-xs">|</span>
                <span className="text-text-tertiary text-sm font-mono">{item.sku}</span>
                <span className="text-xs font-mono bg-panel-border px-2 py-1 text-steel">
                  {category.icon}
                </span>
                {stockStatus && (
                  <span
                    className={`${stockStatus.color} text-xs font-mono uppercase tracking-wider font-semibold`}
                    style={{ textShadow: '0 0 6px currentColor' }}
                  >
                    {stockStatus.status}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAdjustStock(true)}
                className="px-6 py-3 bg-amber/20 hover:bg-amber/30 text-amber font-mono uppercase text-sm tracking-wider transition-all border border-amber/30 hover:border-amber"
              >
                Adjust Stock
              </button>
              <button
                onClick={() => setIsEditing(true)}
                className="px-6 py-3 bg-steel/20 hover:bg-steel/30 text-steel font-mono uppercase text-sm tracking-wider transition-all border border-steel/30 hover:border-steel"
              >
                Edit
              </button>
              <button
                onClick={() => setShowDeleteConfirm(true)}
                className="px-6 py-3 bg-error-red/20 hover:bg-error-red/30 text-error-red font-mono uppercase text-sm tracking-wider transition-all border border-error-red/30 hover:border-error-red"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Product Info */}
        <InfoPanel title="Product Information">
          <InfoRow label="Name" value={item.name} />
          <InfoRow label="SKU" value={item.sku} />
          <InfoRow label="Category" value={category.label} />
          <InfoRow label="Product Type" value={item.productType} />
          <InfoRow label="Grade" value={item.grade} />
          <InfoRow label="Unit" value={item.unit} />
          <InfoRow label="Description" value={item.description} />
        </InfoPanel>

        {/* Pricing */}
        <InfoPanel title="Pricing">
          <InfoRow
            label="Cost Per Unit"
            value={item.costPerUnit ? `$${Number(item.costPerUnit).toFixed(4)}` : null}
          />
          <InfoRow
            label="Price Per Unit"
            value={item.pricePerUnit ? `$${Number(item.pricePerUnit).toFixed(4)}` : null}
            highlight
          />
          {item.costPerUnit && item.pricePerUnit && (
            <InfoRow
              label="Margin"
              value={`$${(Number(item.pricePerUnit) - Number(item.costPerUnit)).toFixed(4)} (${(((Number(item.pricePerUnit) - Number(item.costPerUnit)) / Number(item.costPerUnit)) * 100).toFixed(1)}%)`}
            />
          )}
        </InfoPanel>

        {/* Inventory */}
        <InfoPanel title="Inventory Tracking">
          <InfoRow
            label="Current Stock"
            value={`${Number(item.currentStock).toLocaleString()} ${item.unit}s`}
            highlight
          />
          <InfoRow
            label="Reorder Point"
            value={item.reorderPoint ? `${Number(item.reorderPoint).toLocaleString()} ${item.unit}s` : null}
          />
          <InfoRow
            label="Reorder Quantity"
            value={item.reorderQuantity ? `${Number(item.reorderQuantity).toLocaleString()} ${item.unit}s` : null}
          />
          <InfoRow label="Tracking" value={item.isTracked ? 'Enabled' : 'Disabled'} />
          <InfoRow label="Status" value={item.isActive ? 'Active' : 'Inactive'} />
        </InfoPanel>

        {/* Notes */}
        <InfoPanel title="Notes">
          <div className="text-text-primary font-mono text-sm whitespace-pre-wrap">
            {item.notes || <span className="text-text-tertiary italic">No notes</span>}
          </div>
        </InfoPanel>
      </div>

      {/* Timestamps */}
      <div className="text-text-tertiary text-xs font-mono flex items-center space-x-6">
        <span>Created: {new Date(item.createdAt).toLocaleString()}</span>
        <span>Updated: {new Date(item.updatedAt).toLocaleString()}</span>
      </div>

      {/* Adjust Stock Modal */}
      {showAdjustStock && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative stat-card max-w-md w-full">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-amber" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-amber" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-amber" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-amber" />

            <div className="bg-panel-dark border border-amber/50 p-8 clip-path-octagon">
              <h3 className="text-2xl font-display text-amber mb-4 uppercase">Adjust Stock</h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Current stock: <strong>{Number(item.currentStock).toLocaleString()} {item.unit}s</strong>
              </p>

              {adjustmentError && (
                <div className="mb-4 p-3 bg-error-red/10 border border-error-red/50">
                  <p className="text-error-red text-sm font-mono">&gt; {adjustmentError}</p>
                </div>
              )}

              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Adjustment Quantity
                  </label>
                  <input
                    type="number"
                    value={adjustmentQty}
                    onChange={(e) => setAdjustmentQty(e.target.value)}
                    step="0.01"
                    placeholder="Use negative for reduction"
                    className="form-input"
                  />
                  <p className="text-text-tertiary text-xs font-mono mt-1">
                    Positive = add stock, Negative = remove stock
                  </p>
                </div>
                <div>
                  <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                    Reason (Optional)
                  </label>
                  <input
                    type="text"
                    value={adjustmentReason}
                    onChange={(e) => setAdjustmentReason(e.target.value)}
                    placeholder="e.g., Physical count, Spillage, Received shipment"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowAdjustStock(false);
                    setAdjustmentQty('');
                    setAdjustmentReason('');
                    setAdjustmentError('');
                  }}
                  className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAdjustStock}
                  className="px-6 py-3 bg-amber hover:bg-amber/90 text-background font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Apply Adjustment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="relative stat-card max-w-md w-full">
            <div className="absolute top-0 left-0 w-4 h-4 border-l-2 border-t-2 border-error-red" />
            <div className="absolute top-0 right-0 w-4 h-4 border-r-2 border-t-2 border-error-red" />
            <div className="absolute bottom-0 left-0 w-4 h-4 border-l-2 border-b-2 border-error-red" />
            <div className="absolute bottom-0 right-0 w-4 h-4 border-r-2 border-b-2 border-error-red" />

            <div className="bg-panel-dark border border-error-red/50 p-8 clip-path-octagon">
              <h3 className="text-2xl font-display text-error-red mb-4 uppercase">Confirm Delete</h3>
              <p className="text-text-secondary font-mono text-sm mb-6">
                Are you sure you want to delete <strong>{item.name}</strong>? This action cannot be
                undone.
              </p>

              {deleteError && (
                <div className="mb-4 p-3 bg-error-red/10 border border-error-red/50">
                  <p className="text-error-red text-sm font-mono">&gt; {deleteError}</p>
                </div>
              )}

              <div className="flex items-center justify-end space-x-4">
                <button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteError('');
                  }}
                  className="px-6 py-3 bg-panel-border hover:bg-steel/20 text-text-primary font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  className="px-6 py-3 bg-error-red hover:bg-error-red/90 text-white font-mono uppercase text-sm tracking-wider transition-all"
                >
                  Delete Product
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Info panel with geometric styling
 */
function InfoPanel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="relative stat-card">
      <div className="absolute top-0 left-0 w-3 h-3 border-l-2 border-t-2 border-amber" />
      <div className="absolute top-0 right-0 w-3 h-3 border-r-2 border-t-2 border-amber" />
      <div className="absolute bottom-0 left-0 w-3 h-3 border-l-2 border-b-2 border-amber" />
      <div className="absolute bottom-0 right-0 w-3 h-3 border-r-2 border-b-2 border-amber" />

      <div className="bg-panel-dark border border-panel-border p-6 clip-path-octagon">
        <div className="flex items-center space-x-2 mb-6">
          <div className="w-1 h-6 bg-gradient-to-b from-amber to-transparent" />
          <h3 className="text-xl font-display text-text-primary uppercase tracking-wide">
            {title}
          </h3>
        </div>
        <div className="space-y-4">{children}</div>
      </div>
    </div>
  );
}

/**
 * Info row for displaying label/value pairs
 */
function InfoRow({
  label,
  value,
  highlight,
}: {
  label: string;
  value: string | null | undefined;
  highlight?: boolean;
}) {
  return (
    <div className="flex justify-between items-center border-b border-panel-border pb-3 last:border-0 last:pb-0">
      <span className="text-text-secondary text-xs uppercase tracking-widest font-mono">
        {label}
      </span>
      <span
        className={`font-mono ${highlight ? 'text-amber font-semibold' : 'text-text-primary'}`}
        style={highlight ? { textShadow: '0 0 6px rgba(255, 149, 0, 0.4)' } : undefined}
      >
        {value || <span className="text-text-tertiary">-</span>}
      </span>
    </div>
  );
}

export default InventoryDetailPage;
